import React, { useState, useEffect } from 'react';
import { theme } from '../styles/theme';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { Badge } from '../components/Badge';
import { BookIcon, UsersIcon, ArrowRightIcon } from '../components/Icons';
import { API_ENDPOINTS, getAuthHeaders } from '../config/api';

export const WriterDashboardPage = () => {
  const [userName, setUserName] = useState('Writer');
  const [stats, setStats] = useState({
    manuscripts: 0,
    betaReaders: 0,
    queries: 0,
  });
  const [recentActivity, setRecentActivity] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    // Get user name from localStorage
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        setUserName(user.name || user.email?.split('@')[0] || 'Writer');
      } catch (err) {
        console.error('Error parsing user:', err);
      }
    }

    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    setError('');

    try {
      // Fetch manuscripts and queries in parallel
      const [manuscriptsRes, queriesRes] = await Promise.all([
        fetch(API_ENDPOINTS.manuscripts.list, { headers: getAuthHeaders() }),
        fetch(API_ENDPOINTS.queries.myQueries, { headers: getAuthHeaders() }),
      ]);

      const manuscriptsData = await manuscriptsRes.json();
      const queriesData = await queriesRes.json();

      if (!manuscriptsRes.ok) {
        setError(manuscriptsData.error || 'Failed to fetch dashboard data');
        setLoading(false);
        return;
      }

      // Calculate stats
      const manuscripts = manuscriptsData.manuscripts || [];
      const queries = queriesData.queries || [];

      // Sum up all beta readers across all manuscripts
      const totalBetaReaders = manuscripts.reduce((sum: number, m: any) => {
        return sum + (m._count?.betaApplications || 0);
      }, 0);

      setStats({
        manuscripts: manuscripts.length,
        betaReaders: totalBetaReaders,
        queries: queries.length,
      });

      // Build recent activity from manuscripts and queries
      const activities: any[] = [];

      // Add manuscript creation activities
      manuscripts.slice(0, 3).forEach((m: any) => {
        const createdDate = new Date(m.createdAt);
        const daysAgo = Math.floor((new Date().getTime() - createdDate.getTime()) / (1000 * 60 * 60 * 24));
        const timeStr = daysAgo === 0 ? 'Today' : daysAgo === 1 ? 'Yesterday' : `${daysAgo} days ago`;

        activities.push({
          message: `Created manuscript "${m.title}"`,
          time: timeStr,
          date: createdDate,
        });
      });

      // Add query submission activities
      queries.slice(0, 3).forEach((q: any) => {
        const createdDate = new Date(q.createdAt);
        const daysAgo = Math.floor((new Date().getTime() - createdDate.getTime()) / (1000 * 60 * 60 * 24));
        const timeStr = daysAgo === 0 ? 'Today' : daysAgo === 1 ? 'Yesterday' : `${daysAgo} days ago`;

        activities.push({
          message: `Submitted query to ${q.agent?.displayName || 'an agent'} for "${q.manuscript?.title || 'manuscript'}"`,
          time: timeStr,
          date: createdDate,
        });
      });

      // Sort by date (most recent first) and take top 5
      activities.sort((a, b) => b.date.getTime() - a.date.getTime());
      setRecentActivity(activities.slice(0, 5));

      setLoading(false);
    } catch (err) {
      console.error('Fetch dashboard data error:', err);
      setError('Network error. Please check your connection and try again.');
      setLoading(false);
    }
  };


  const quickActions = [
    { title: 'Add Manuscript', description: 'Upload a new manuscript', link: '/writer/manuscripts/new', icon: <BookIcon /> },
    { title: 'View Manuscripts', description: 'Manage your works', link: '/writer/manuscripts', icon: <BookIcon /> },
    { title: 'Beta Readers', description: 'See active readers', link: '/writer/beta-readers', icon: <UsersIcon /> },
    { title: 'My Queries', description: 'Track submissions', link: '/writer/queries', icon: <ArrowRightIcon /> },
  ];

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: theme.colors.background,
      fontFamily: theme.fonts.primary,
    }}>
      <Header />

      <main style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '60px 40px',
      }}>
        {/* Welcome Message */}
        <div style={{
          marginBottom: '40px',
        }}>
          <h1 style={{
            fontSize: '40px',
            fontWeight: '400',
            marginBottom: '8px',
            color: theme.colors.textPrimary,
          }}>
            Welcome back, {userName}
          </h1>
          <p style={{
            fontSize: '18px',
            color: theme.colors.textSecondary,
          }}>
            Here's what's happening with your manuscripts
          </p>
        </div>

        {/* Loading State */}
        {loading && (
          <Card>
            <div style={{
              textAlign: 'center',
              padding: '60px 40px',
            }}>
              <p style={{
                fontSize: '18px',
                color: theme.colors.textSecondary,
              }}>
                Loading your dashboard...
              </p>
            </div>
          </Card>
        )}

        {/* Error State */}
        {!loading && error && (
          <Card>
            <div style={{
              textAlign: 'center',
              padding: '40px',
            }}>
              <p style={{
                fontSize: '18px',
                color: theme.colors.error,
                marginBottom: '20px',
              }}>
                {error}
              </p>
              <Button onClick={fetchDashboardData}>
                Try Again
              </Button>
            </div>
          </Card>
        )}

        {/* Quick Stats */}
        {!loading && !error && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '24px',
          marginBottom: '48px',
        }}>
          <Card>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
              <div>
                <div style={{
                  fontSize: '14px',
                  fontWeight: '500',
                  color: theme.colors.textSecondary,
                  marginBottom: '8px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  fontFamily: theme.fonts.monospace,
                }}>
                  Manuscripts
                </div>
                <div style={{
                  fontSize: '48px',
                  fontWeight: '400',
                  color: theme.colors.primary,
                  fontFamily: theme.fonts.monospace,
                }}>
                  {stats.manuscripts}
                </div>
              </div>
              <div style={{
                width: '64px',
                height: '64px',
                backgroundColor: theme.colors.primaryLight,
                borderRadius: theme.borderRadius.large,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: theme.colors.primary,
              }}>
                <BookIcon width={32} height={32} />
              </div>
            </div>
          </Card>

          <Card>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
              <div>
                <div style={{
                  fontSize: '14px',
                  fontWeight: '500',
                  color: theme.colors.textSecondary,
                  marginBottom: '8px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  fontFamily: theme.fonts.monospace,
                }}>
                  Beta Readers
                </div>
                <div style={{
                  fontSize: '48px',
                  fontWeight: '400',
                  color: theme.colors.primary,
                  fontFamily: theme.fonts.monospace,
                }}>
                  {stats.betaReaders}
                </div>
              </div>
              <div style={{
                width: '64px',
                height: '64px',
                backgroundColor: theme.colors.primaryLight,
                borderRadius: theme.borderRadius.large,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: theme.colors.primary,
              }}>
                <UsersIcon width={32} height={32} />
              </div>
            </div>
          </Card>

          <Card>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
              <div>
                <div style={{
                  fontSize: '14px',
                  fontWeight: '500',
                  color: theme.colors.textSecondary,
                  marginBottom: '8px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  fontFamily: theme.fonts.monospace,
                }}>
                  Queries Sent
                </div>
                <div style={{
                  fontSize: '48px',
                  fontWeight: '400',
                  color: theme.colors.primary,
                  fontFamily: theme.fonts.monospace,
                }}>
                  {stats.queries}
                </div>
              </div>
              <div style={{
                width: '64px',
                height: '64px',
                backgroundColor: theme.colors.primaryLight,
                borderRadius: theme.borderRadius.large,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: theme.colors.primary,
              }}>
                <ArrowRightIcon width={32} height={32} />
              </div>
            </div>
          </Card>
        </div>
        )}

        {/* Main Content Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '2fr 1fr',
          gap: '32px',
          marginBottom: '48px',
        }}>
          {/* Recent Activity Feed */}
          <div>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '24px',
            }}>
              <h2 style={{
                fontSize: '28px',
                fontWeight: '400',
                color: theme.colors.textPrimary,
                margin: 0,
              }}>
                Recent Activity
              </h2>
              <Button variant="ghost">View All</Button>
            </div>

            <Card>
              {recentActivity.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  padding: '40px 20px',
                  color: theme.colors.textSecondary,
                }}>
                  <p style={{ fontSize: '16px', marginBottom: '8px' }}>No recent activity</p>
                  <p style={{ fontSize: '14px' }}>Start by creating a new manuscript or submitting a query</p>
                </div>
              ) : (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '20px',
                }}>
                  {recentActivity.map((activity, index) => (
                  <div key={index} style={{
                    paddingBottom: index < recentActivity.length - 1 ? '20px' : '0',
                    borderBottom: index < recentActivity.length - 1 ? `1px solid ${theme.colors.border}` : 'none',
                  }}>
                    <div style={{
                      fontSize: '15px',
                      lineHeight: '1.6',
                      color: theme.colors.textPrimary,
                      marginBottom: '4px',
                    }}>
                      {activity.message}
                    </div>
                    <div style={{
                      fontSize: '13px',
                      color: theme.colors.textTertiary,
                      fontFamily: theme.fonts.monospace,
                    }}>
                      {activity.time}
                    </div>
                  </div>
                  ))}
                </div>
              )}
            </Card>
          </div>

          {/* Quick Actions */}
          <div>
            <h2 style={{
              fontSize: '28px',
              fontWeight: '400',
              color: theme.colors.textPrimary,
              marginBottom: '24px',
            }}>
              Quick Actions
            </h2>

            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
            }}>
              {quickActions.map((action, index) => (
                <Card key={index} hoverable onClick={() => window.location.href = action.link}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                  }}>
                    <div style={{
                      width: '48px',
                      height: '48px',
                      backgroundColor: theme.colors.primaryLight,
                      borderRadius: theme.borderRadius.medium,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: theme.colors.primary,
                      flexShrink: 0,
                    }}>
                      {action.icon}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{
                        fontSize: '16px',
                        fontWeight: '500',
                        color: theme.colors.textPrimary,
                        marginBottom: '4px',
                      }}>
                        {action.title}
                      </div>
                      <div style={{
                        fontSize: '14px',
                        color: theme.colors.textSecondary,
                      }}>
                        {action.description}
                      </div>
                    </div>
                    <ArrowRightIcon width={20} height={20} />
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </div>

        {/* Navigation to Main Sections */}
        <div>
          <h2 style={{
            fontSize: '28px',
            fontWeight: '400',
            color: theme.colors.textPrimary,
            marginBottom: '24px',
          }}>
            Manage Your Work
          </h2>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '24px',
          }}>
            {[
              {
                title: 'Manuscripts',
                description: 'View and manage all your manuscripts',
                link: '/writer/manuscripts',
                badge: `${stats.manuscripts} ${stats.manuscripts === 1 ? 'manuscript' : 'manuscripts'}`,
              },
              {
                title: 'Beta Readers',
                description: 'Manage your beta reader relationships',
                link: '/writer/beta-readers',
                badge: `${stats.betaReaders} ${stats.betaReaders === 1 ? 'reader' : 'readers'}`,
              },
              {
                title: 'Queries',
                description: 'Track your agent submissions',
                link: '/writer/queries',
                badge: `${stats.queries} ${stats.queries === 1 ? 'query' : 'queries'}`,
              },
            ].map((section, index) => (
              <Card key={index} hoverable onClick={() => window.location.href = section.link}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: '12px',
                }}>
                  <h3 style={{
                    fontSize: '22px',
                    fontWeight: '500',
                    color: theme.colors.textPrimary,
                    margin: 0,
                  }}>
                    {section.title}
                  </h3>
                  <Badge variant="primary">{section.badge}</Badge>
                </div>
                <p style={{
                  fontSize: '16px',
                  lineHeight: '1.6',
                  color: theme.colors.textSecondary,
                  marginBottom: '16px',
                }}>
                  {section.description}
                </p>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  color: theme.colors.primary,
                  fontSize: '15px',
                  fontWeight: '500',
                }}>
                  View all <ArrowRightIcon width={16} height={16} />
                </div>
              </Card>
            ))}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};
